import { Id } from '@common/types';
import { buildUrl, handleRequest, handleFileRequest } from '@common/services/requestHelper';
import { RequestType } from '@common/services/types';
import { DocumentType } from '@common/types';

const basePath = process.env.REACT_APP_API_PATH ?? '';

const fetchSubmission = async (id: string) => {
  const url = buildUrl(`/submission/${id}`, basePath);

  return handleRequest({ url, requestType: RequestType.GET });
};

const updateSubmission = async (id: string, dataSet: unknown) => {
  const url = buildUrl(`/submission/${id}`, basePath);

  return handleRequest({
    url,
    requestType: RequestType.PATCH,
    data: dataSet,
  });
};

const updateSubmissionActiveIngredients = async (id: string, dataSet: unknown) => {
  const url = buildUrl(`/submission/${id}/active-ingredients`, basePath);

  return handleRequest({
    url,
    requestType: RequestType.PATCH,
    data: dataSet,
  });
};

const updateSubmissionSubstances = async (id: string, dataSet: unknown) => {
  const url = buildUrl(`/submission/${id}/substances`, basePath);

  return handleRequest({
    url,
    requestType: RequestType.PATCH,
    data: dataSet,
  });
};

const fetchCompanySubmissions = async (queryString: string) => {
  const url = buildUrl(`/submission`, basePath, queryString);

  return handleRequest({ url, requestType: RequestType.GET });
};

const postDraftFromProductGroup = async (dataSet: unknown) => {
  const url = buildUrl('/submission/draft/product-group', basePath);

  return handleRequest({
    url,
    requestType: RequestType.POST,
    data: dataSet,
  });
};



const generateProductGroupPlan = async (companyId: string, dataSet: unknown) => {
  const url = buildUrl('/product-group/plan', basePath);

  return handleRequest({
    url,
    requestType: RequestType.POST,
    data: dataSet,
    companyId: companyId,
  });
};

const generateProductFamilyPlan = async (dataSet: unknown) => {
  const url = buildUrl('/product-family/plan', basePath);

  return handleRequest({
    url,
    requestType: RequestType.POST,
    data: dataSet,
  });
};

const submitSubmission = async (submissionId: string, dataSet: unknown) => {
  const url = buildUrl(`/submission/${submissionId}/submit`, basePath);

  return handleRequest({
    url,
    requestType: RequestType.PATCH,
    data: dataSet,
  });
};

const validateSubmission = async (submissionId: string) => {
  const url = buildUrl(`/submission/${submissionId}/validate`, basePath);

  return handleRequest({ url, requestType: RequestType.GET });
};

const cancelSubmission = async (submissionId: number, dataSet: unknown) => {
  const url = buildUrl(`/submission/${submissionId}/cancel`, basePath);

  return handleRequest({ url, requestType: RequestType.POST, data: dataSet });
};

const removeSubmissionDocument = async (submissionId: Id, documentId: Id) => {
  const url = buildUrl(`/submission/${submissionId}/document/${documentId}`, basePath);

  return handleRequest({
    url,
    requestType: RequestType.DELETE,
    data: {},
  });
};

const createProduct = async (submissionId: Id, dataSet: unknown) => {
  const url = buildUrl(`/submission/${submissionId}/product`, basePath);

  return handleRequest({
    url,
    requestType: RequestType.POST,
    data: dataSet,
  });
};

const updateProduct = async (submissionId: Id, productId: Id, dataSet: unknown) => {
  const url = buildUrl(`/submission/${submissionId}/product/${productId}`, basePath);

  return handleRequest({ url, requestType: RequestType.PATCH, data: dataSet });
};

const removeProduct = async (submissionId: Id, productId: Id) => {
  const url = buildUrl(`/submission/${submissionId}/product/${productId}`, basePath);

  return handleRequest({
    url,
    requestType: RequestType.DELETE,
    data: {},
  });
};

const updateAssociatedContent = async (submissionId: Id, productId: string, dataSet: unknown) => {
  const url = buildUrl(`/submission/${submissionId}/product/${productId}/document-assignments/`, basePath);

  return handleRequest({ url, requestType: RequestType.POST, data: dataSet });
};

const addDocument = async (submissionId: Id, dataSet: unknown) => {
  const url = buildUrl(`/submission/${submissionId}/document`, basePath);

  return handleRequest({ url, requestType: RequestType.POST, data: dataSet });
};

const addDocumentVersion = async (submissionId: Id, documentId: Id, dataSet: unknown) => {
  const url = buildUrl(`/submission/${submissionId}/document/${documentId}`, basePath);

  return handleRequest({ url, requestType: RequestType.POST, data: dataSet });
};

const updateDocument = async (submissionId: Id, documentId: Id, dataSet: unknown) => {
  const url = buildUrl(`/submission/${submissionId}/document/${documentId}`, basePath);

  return handleRequest({ url, requestType: RequestType.PUT, data: dataSet });
};

const completeRetireDocument = async (submissionId: Id, documentId: Id, documentType: DocumentType, dataSet: unknown) => {
  const url = buildUrl(`/submission/${submissionId}/retire-document/${documentId}/type/${documentType}`, basePath);

  return handleRequest({ url, requestType: RequestType.PUT, data: dataSet });
};

const prepareDocumentForRetire = async (submissionId: number, documentId: string, documentType: DocumentType, dataSet: unknown) => {
  const url = buildUrl(`/submission/${submissionId}/document/${documentId}/type/${documentType}/retire`, basePath);

  return handleRequest({ url, requestType: RequestType.PUT, data: dataSet });
};

const downloadTaskFile = async (taskId: Id) => {
  const url = buildUrl(`/submission-task/${taskId}/original-file`, basePath);

  return handleFileRequest({ url, requestType: RequestType.GET });
};

const downloadTaskFileByRole = async (taskId: Id, fileRole: string, propagateNotFound?: boolean) => {
  const url = buildUrl(`/submission-task/${taskId}/fileRole/${fileRole}/file`, basePath);

  return handleFileRequest({ url, requestType: RequestType.GET, propagateNotFound: propagateNotFound ?? true });
};

const downloadNoteFile = async (taskId: Id, noteId: number, dataSet: unknown, propagateNotFound?: boolean) => {
  const url = buildUrl(`/submission-task/${taskId}/note/${noteId}/file`, basePath);

  return handleFileRequest({ url, requestType: RequestType.GET, data: dataSet, propagateNotFound: propagateNotFound ?? true });
};

const replaceTaskContent = async (taskId: Id, dataSet: unknown) => {
  const url = buildUrl(`/submission-task/${taskId}/content`, basePath);

  return handleRequest({ url, requestType: RequestType.PUT, data: dataSet });
};

const downloadDocumentFile = async (submissionId: Id, documentId: Id) => {
  const url = buildUrl(`/submission/${submissionId}/document/${documentId}/original-file`, basePath);

  return handleFileRequest({ url, requestType: RequestType.GET });
};

const downloadDocumentFileWithDocumentType = async (submissionId: Id, documentId: Id, documentType: DocumentType) => {
  const url = buildUrl(`/submission/${submissionId}/document/${documentId}/original-file/${documentType}`, basePath);

  return handleFileRequest({ url, requestType: RequestType.GET });
};

const discontinueProduct = async (submissionId: Id, productId: Id, dataSet: unknown) => {
  const url = buildUrl(`/submission/${submissionId}/product/${productId}/discontinuation`, basePath);

  return handleRequest({ url, requestType: RequestType.POST, data: dataSet });
};

const retireProduct = async (submissionId: Id, productId: Id, dataSet: unknown) => {
  const url = buildUrl(`/submission/${submissionId}/product/${productId}/retirement`, basePath);

  return handleRequest({ url, requestType: RequestType.POST, data: dataSet });
};

const reinstateProduct = async (submissionId: Id, productId: Id) => {
  const url = buildUrl(`/submission/${submissionId}/product/${productId}/reinstatement`, basePath);

  return handleRequest({ url, requestType: RequestType.POST });
};

const softDeleteSubmission = async (submissionId: Id) => {
  const url = buildUrl(`/submission/${submissionId}`, basePath);

  return handleRequest({ url, requestType: RequestType.DELETE, data: {} });
};

const getProductReportData = async (queryString: string) => {
  const url = buildUrl(`/report/product`, basePath, queryString);

  return handleFileRequest({ url, requestType: RequestType.GET, data: {} });
};

const getBroadcastAuditReportData = async (queryString: string) => {
  const url = buildUrl(`/report/broadcast-audit`, basePath, queryString);
  return handleFileRequest({ url, requestType: RequestType.GET, data: {} });
};

const getContentReportData = async (queryString: string) => {
  const url = buildUrl(`/report/content`, basePath, queryString);

  return handleFileRequest({ url, requestType: RequestType.GET, data: {} });
};

const getSubmissionDocumentHtmlContent = async (submissionId: string, documentId: string) => {
  const url = buildUrl(`/submission/${submissionId}/document/${documentId}/html-content`, basePath);

  return handleRequest({ url, requestType: RequestType.GET });
};

const fetchSubmissionTaskDetailsByDocumentAndType = async (submissionId: Id, documentId: Id, contentType: string) => {
  const url = buildUrl(`/submission/${submissionId}/document/${documentId}/type/${contentType}/task`, basePath);

  return handleRequest({ url, requestType: RequestType.GET, propagateNotFound: false });
};

export {
  addDocument,
  addDocumentVersion,
  updateDocument,
  prepareDocumentForRetire,
  fetchSubmission,
  fetchCompanySubmissions,
  postDraftFromProductGroup,
  updateSubmission,
  updateSubmissionSubstances,
  submitSubmission,
  validateSubmission,
  cancelSubmission,
  removeSubmissionDocument,
  createProduct,
  updateProduct,
  removeProduct,
  updateAssociatedContent,
  downloadTaskFile,
  downloadTaskFileByRole,
  replaceTaskContent,
  downloadDocumentFile,
  discontinueProduct,
  completeRetireDocument,
  reinstateProduct,
  softDeleteSubmission,
  fetchSubmissionTaskDetailsByDocumentAndType,
  getProductReportData,
  getContentReportData,
  getBroadcastAuditReportData,
  generateProductGroupPlan,
  generateProductFamilyPlan,
  downloadNoteFile,
  updateSubmissionActiveIngredients,
  retireProduct,
  downloadDocumentFileWithDocumentType,
  getSubmissionDocumentHtmlContent,
};
